import React from "react";
import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionCard,
  DecisionList,
} from "@/components/DecisionPageLayout";
import { ClipboardCheck, AlertCircle, Stethoscope } from "lucide-react";
import { InlineRelatedLinks } from "@/components/RelatedPages";

const AmIACandidate = () => (
  <DecisionPageLayout
    seo={{
      title:
        "Am I a Candidate for SAINT TMS? | Neuro Wellness Coral Springs, FL",
      description:
        "Plain-language screening for SAINT TMS eligibility — medical history factors, what we need to know, and why only a clinical team can determine candidacy.",
      canonical: "/am-i-a-candidate",
      keywords:
        "SAINT TMS candidate, TMS eligibility, TMS contraindications, who qualifies for SAINT therapy",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: "Am I a Candidate?",
        about: "Eligibility screening for SAINT TMS therapy",
      },
    }}
    badge="Candidacy Screening"
    title="Am I a Candidate?"
    subtitle="These are things we need to know — not automatic disqualifiers. Only a clinical team can make the determination."
  >
    <DecisionSection heading="Things We Need to Know">
      <p>
        The following are factors your clinical team will review. None of them
        automatically disqualify you. They are information we need to make a
        safe, informed decision about whether SAINT is appropriate for you.
      </p>
    </DecisionSection>

    <DecisionCard>
      <div className="flex items-center gap-3">
        <ClipboardCheck className="h-6 w-6 text-accent shrink-0" />
        <DecisionSubHeading>Medical History Factors</DecisionSubHeading>
      </div>
      <DecisionList
        items={[
          "Any metal in your head or neck (implants, clips, shunts, plates, screws)",
          "A pacemaker or any implanted electronic device",
          "A history of seizures or epilepsy",
          "A family history of seizures",
          "A bipolar disorder diagnosis",
          "Current medications, including all prescriptions and supplements",
          "Whether you have had prior TMS treatment, and when",
        ]}
      />
    </DecisionCard>

    <DecisionSection>
      <div className="flex gap-3 items-start">
        <AlertCircle className="h-5 w-5 text-accent shrink-0 mt-1" />
        <div className="space-y-3">
          <DecisionSubHeading>Why These Matter</DecisionSubHeading>
          <p>
            TMS uses magnetic pulses. Metal implants near the treatment area and
            certain implanted devices can be affected by magnetic fields. A
            history of seizures matters because magnetic stimulation can, in
            rare cases, affect seizure risk. A bipolar diagnosis matters because
            brain stimulation works differently with bipolar depression than
            with standard depression.
          </p>
          <p>
            None of these are automatic disqualifiers. Some may require
            additional evaluation or a modified protocol. The clinical team
            determines this — not a website, and not a coordinator.
          </p>
        </div>
      </div>
    </DecisionSection>

    <DecisionSection>
      <div className="flex gap-3 items-start">
        <Stethoscope className="h-5 w-5 text-accent shrink-0 mt-1" />
        <div className="space-y-3">
          <DecisionSubHeading>How the Decision Gets Made</DecisionSubHeading>
          <p>
            Candidacy is determined by a clinical team reviewing your full
            medical history. The consultation is where that review happens. You
            will be asked detailed questions, and you should answer them
            honestly and completely.
          </p>
          <p>
            We do not state that any treatment carries no risk. Every medical
            treatment has potential risks and benefits, and the consultation is
            where those are discussed in detail for your specific situation.
          </p>
        </div>
      </div>
    </DecisionSection>

    <DecisionSection>
      <DecisionSubHeading>
        What to Expect at the Consultation
      </DecisionSubHeading>
      <p>
        The consultation is a conversation, not a commitment. You will speak
        with a member of our clinical team. They will review your history,
        answer your questions, and give you an honest assessment of whether
        SAINT is a fit — or whether a different treatment may be more
        appropriate.
      </p>
    </DecisionSection>

    <InlineRelatedLinks
      heading="Explore Related Pages"
      links={[
        { title: "SAINT® Accelerated TMS", href: "/saint" },
        {
          title: "SAINT vs. Standard TMS",
          href: "/learn/saint-vs-traditional-tms",
        },
        { title: "I've Already Tried TMS", href: "/already-tried-tms" },
        { title: "How Insurance Works", href: "/how-insurance-works" },
        { title: "Compare All Treatments", href: "/compare-treatments" },
      ]}
    />
  </DecisionPageLayout>
);

export default AmIACandidate;
